import * as Yup from 'yup';
import dayjs from 'dayjs';

import { getCurrentWeek } from 'utils/date';
import type { FreelancerAvailableTime } from 'state/remote/types';
import { createLocale } from 'locales';

const localize = createLocale('freelancerAvailabilityLocales');
const globalLocalize = createLocale('global');

export const initialValues = {
  availability: [] as Array<FreelancerAvailableTime>,
  'current-week': getCurrentWeek()[0].format('YYYY-MM-DD'),
  'weekly-schedule-repeat': { date: undefined, isEnabled: false },
};

/**
 * validation rules are as follow:
 *      - If start(or end) is defined, end(or start) should be defined
 *      - If start and end are both defined, start must be strictly less than end
 *      - When repeat is enabled, repeat date should be less than 6 months
 */
export const validationSchema = Yup.object().shape({
  availability: Yup.array().of(
    Yup.object().shape(
      {
        available: Yup.bool().optional(),
        date: Yup.string().optional(),
        end: Yup.string().when('start', {
          is: (value: unknown) => typeof value === 'string',
          then: Yup.string()
            .required(localize('enterBothStartAndEndTimeOrLeaveBothBlank'))
            .nullable(true),
          // eslint-disable-next-line sort-keys
          otherwise: Yup.string().nullable(true).notRequired(),
        }),
        start: Yup.string().when('end', {
          is: (value: unknown) => typeof value === 'string',
          then: Yup.string()
            .nullable(true)
            .required(localize('enterBothStartAndEndTimeOrLeaveBothBlank'))
            .test(
              'is-start-less-than-end',
              localize('startDateMustBeLessThanEndDate'),
              (value, ctx) => (value as string) < ctx.parent.end,
            ),
          // eslint-disable-next-line sort-keys
          otherwise: Yup.string().nullable(true).notRequired(),
        }),
      },
      [['start', 'end']],
    ),
  ),
  'current-week': Yup.string().required(
    globalLocalize('fieldRequiredErrorMessage'),
  ),
  'weekly-schedule-repeat': Yup.object().shape({
    date: Yup.string().when('isEnabled', {
      is: (value: boolean) => value === true,
      then: Yup.string()
        .required(localize('endDateIsRequiredWhenRepeating'))
        .test(
          'date-less-than-six-months',
          localize('repeatDateMustBeLessThan6Months'),
          (value) => {
            return (
              dayjs().isBefore(dayjs(value)) &&
              dayjs(value).isBefore(dayjs().add(6, 'months'))
            );
          },
        ),
      // eslint-disable-next-line sort-keys
      otherwise: Yup.string().nullable(true).notRequired(),
    }),
    isEnabled: Yup.bool().required(),
  }),
});
